import { reactive, toRefs, watch } from 'vue';
import { useApiAuth } from '@/hooks';
import {
  ITaxonomy,
  IIssueCategory,
  IIssueType,
  IResolution,
  ISupplier,
  ICategory,
  ISupportRequestIssueType,
  ISubIssueType,
} from '@/types';

interface ITaxonomyState extends ITaxonomy {
  publicResolutions: IResolution[];
  loading: boolean;
  error: string | null;
}

const taxonomyState = reactive<ITaxonomyState>({
  categories: [],
  resolutions: [],
  publicResolutions: [],
  tags: [],
  suppliers: [],
  claimTypes: [],
  statuses: [],
  severity: [],
  priorities: [],
  supportRequestIssueTypes: [],
  loading: false,
  error: null,
});

export const useTaxonomy = () => {
  const { loading, data, error, get: getTaxonomy } = useApiAuth<ITaxonomy>('/taxonomy');

  watch([loading, data, error], () => {
    taxonomyState.categories = data.value?.categories || [];
    taxonomyState.resolutions = data.value?.resolutions || [];
    taxonomyState.tags = data.value?.tags || [];
    taxonomyState.suppliers = data.value?.suppliers || [];
    taxonomyState.claimTypes = data.value?.claimTypes || [];
    taxonomyState.statuses = data.value?.statuses || [];
    taxonomyState.severity = data.value?.severity || [];
    taxonomyState.priorities = data.value?.priorities || [];
    taxonomyState.supportRequestIssueTypes = data.value?.supportRequestIssueTypes || [];
    taxonomyState.loading = loading.value;
    taxonomyState.error = error.value;
  });

  const getCategoryTypes = (categoryId: number): IIssueCategory[] => {
    if (!taxonomyState.categories) return [];

    const category = taxonomyState.categories.find(
      (category: ICategory) => category.id === categoryId && category.isActive
    );
    const types = category?.types
      ? category.types.filter((type: IIssueCategory) => type.isActive)
      : [];
    return types;
  };

  const getCategoryTypeIssues = (categoryId: number, categoryTypeId: number): IIssueType[] => {
    if (!taxonomyState.categories) return [];

    const category = taxonomyState.categories.find(
      (category: ICategory) => category.id === categoryId && category.isActive
    );
    const type = category?.types.find(
      (categoryType: IIssueCategory) => categoryType.id === categoryTypeId && categoryType.isActive
    );
    const issues = type?.issues ? type.issues.filter((issue: IIssueType) => issue.isActive) : [];
    return issues;
  };

  const getCategoryTypeIssuesSubIssues = (
    categoryId: number,
    categoryTypeId: number,
    categoryTypeIssueId: number
  ): ISubIssueType[] => {
    if (!taxonomyState.categories) return [];
    const category = taxonomyState.categories.find(
      (category: ICategory) => category.id === categoryId && category.isActive
    );
    const type = category?.types.find(
      (categoryType: IIssueCategory) => categoryType.id === categoryTypeId && categoryType.isActive
    );
    const issue = type?.issues.find(
      (categoryIssue: IIssueType) =>
        categoryIssue.id === categoryTypeIssueId && categoryIssue.isActive
    );
    const subIssues = issue?.subIssues
      ? issue.subIssues.filter((subIssue: ISubIssueType) => subIssue.isActive)
      : [];
    return subIssues;
  };

  const getSupplierName = (supplierId: number): string => {
    if (!taxonomyState.suppliers) return '';

    return (
      taxonomyState.suppliers.find((supplier: ISupplier) => supplier.id === supplierId)?.name || ''
    );
  };

  const getResolutionName = (resolutionId: number): string => {
    if (!taxonomyState.resolutions) return '';

    return (
      taxonomyState.resolutions.find((resolution: IResolution) => resolution.id === resolutionId)
        ?.name || ''
    );
  };

  const getSupportRequestIssueTypeName = (supportRequestIssueTypeId: number): string => {
    if (!taxonomyState.supportRequestIssueTypes) return '';
    return (
      taxonomyState.supportRequestIssueTypes.find(
        (supportRequestIssueType: ISupportRequestIssueType) =>
          supportRequestIssueType.id === supportRequestIssueTypeId
      )?.name || ''
    );
  };

  watch(
    () => taxonomyState.resolutions,
    () => {
      if (taxonomyState.resolutions && taxonomyState.resolutions.length > 0) {
        taxonomyState.publicResolutions = taxonomyState.resolutions.filter(
          (resolution: IResolution) => resolution.isInternal === false
        );
      }
    }
  );

  return {
    ...toRefs(taxonomyState),
    getTaxonomy,
    getCategoryTypes,
    getCategoryTypeIssues,
    getCategoryTypeIssuesSubIssues,
    getSupplierName,
    getResolutionName,
    getSupportRequestIssueTypeName,
  };
};
